import {
  CADENCES,
  type Cadence,
  type EngagementInput,
  type EngagementTier,
} from '@clockwise/clients/sows';
import { INVOICE_FLAG_REASON_LABELS } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { BulkAddProductsSheet } from '@/components/custom/bulk-add-products-sheet';
import { CurrencyInput } from '@/components/custom/currency-input';
import { EngagementRateTable } from '@/components/custom/engagement-rate-table';
import { type ProductDisplayLookup, ProductLabel } from '@/components/custom/product-label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ENGAGEMENT_CADENCE_LABELS } from '@/lib/engagement-format';
import { cn } from '@/lib/utils';

// Radix Select forbids empty-string item values, so "inherit" needs a sentinel
// that maps back to null on change.
const INHERIT = '__inherit__';

/**
 * Editing surface for ONE engagement, whatever its billing model. Renders
 * inside a Sheet — the caller owns open/close and persistence.
 *
 * Shared fields first, then the model-specific block. `value` is the current
 * draft; `onChange` returns the next draft on every edit, and the parent
 * stages drafts until the SOW amendment is saved.
 *
 * NULL is "inherit" on cadence and consolidation, and the controls say so
 * rather than showing a blank: what an engagement inherits is a decision
 * someone made at the customer level, and hiding it behind an empty field
 * makes it look unset.
 */
export function EngagementForm({
  value,
  customerCadenceLabel,
  productLookup,
  roleLookup,
  disabled,
  onChange,
}: {
  value: EngagementInput;
  /** What this engagement's cadence falls back to, for the picker's label. */
  customerCadenceLabel: string;
  productLookup: ProductDisplayLookup;
  roleLookup: (id: string) => string;
  disabled?: boolean;
  onChange: (next: EngagementInput) => void;
}) {
  const [bulkOpen, setBulkOpen] = useState(false);

  const addActivities = (products: { id: string }[]) => {
    const existing = new Set(value.activities.map((a) => a.productId));
    onChange({
      ...value,
      activities: [
        ...value.activities,
        ...products.filter((p) => !existing.has(p.id)).map((p) => ({ productId: p.id })),
      ],
    });
  };
  const removeActivity = (productId: string) => {
    onChange({ ...value, activities: value.activities.filter((a) => a.productId !== productId) });
  };

  return (
    <div className="space-y-6">
      {/* A retainer's cadence and fee are one thought — "$2,000 a month" —
          so they share a row, and the name takes the full width above them.
          The other models price per role or per band, so there is no single
          number to pair the cadence with. */}
      {value.billingModel === 'retainer' ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="engagement-name">Name</Label>
            <Input
              id="engagement-name"
              value={value.name}
              onChange={(e) => onChange({ ...value, name: e.target.value })}
              placeholder="e.g. Bookkeeping"
              disabled={disabled}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="engagement-cadence">Billing cadence</Label>
              <Select
                value={value.billingCadence ?? INHERIT}
                onValueChange={(v) =>
                  onChange({ ...value, billingCadence: v === INHERIT ? null : (v as Cadence) })
                }
                disabled={disabled}
              >
                <SelectTrigger id="engagement-cadence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={INHERIT}>From customer ({customerCadenceLabel})</SelectItem>
                  {CADENCES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {ENGAGEMENT_CADENCE_LABELS[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="engagement-fee">Fee</Label>
              <CurrencyInput
                id="engagement-fee"
                value={value.fee}
                onChange={(fee) => onChange({ ...value, fee })}
                disabled={disabled}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            The fee is charged once per billing period. Changing it is an amendment, which is what
            keeps past invoices priced at what was agreed then.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="engagement-name">Name</Label>
            <Input
              id="engagement-name"
              value={value.name}
              onChange={(e) => onChange({ ...value, name: e.target.value })}
              placeholder="e.g. Bookkeeping"
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="engagement-cadence">Billing cadence</Label>
            <Select
              value={value.billingCadence ?? INHERIT}
              onValueChange={(v) =>
                onChange({ ...value, billingCadence: v === INHERIT ? null : (v as Cadence) })
              }
              disabled={disabled}
            >
              <SelectTrigger id="engagement-cadence">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={INHERIT}>From customer ({customerCadenceLabel})</SelectItem>
                {CADENCES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {ENGAGEMENT_CADENCE_LABELS[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="engagement-description">Description</Label>
        <Input
          id="engagement-description"
          value={value.description ?? ''}
          onChange={(e) => onChange({ ...value, description: e.target.value || null })}
          placeholder="Optional — prints on the invoice line"
          disabled={disabled}
        />
      </div>

      {value.billingModel === 'retainer' ? null : (
        <>
          <Separator />
          {value.billingModel === 'tm' ? (
            <EngagementRateTable
              label="Rates by role"
              rates={value.rates}
              roleLookup={roleLookup}
              disabled={disabled}
              emptyHint="No roles priced yet. Rates are seeded from the SOW's rate card."
              onChange={(rates) => onChange({ ...value, rates })}
            />
          ) : (
            <MetricsFields value={value} disabled={disabled} onChange={onChange} />
          )}
        </>
      )}

      {/* Every model lists activities: the list decides which engagement a
          timesheet entry is attributed to. On a retainer it is also the
          engagement's scope; T&M has no scope (every hour bills at its role's
          rate), so there it only steers attribution — and with it, rates. */}
      <Separator />

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Activities ({value.activities.length})</Label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setBulkOpen(true)}
            disabled={disabled}
          >
            <Plus className="size-4" /> Add activities
          </Button>
        </div>
        {value.activities.length === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            No activities — this engagement covers <span className="font-medium">every</span>{' '}
            activity.{' '}
            {value.billingModel === 'tm'
              ? 'Add some so only that work is attributed to this engagement. T&M has no scope — this only decides attribution.'
              : 'Add some to narrow what counts as in scope.'}
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {value.activities.map((a) => {
              const info = productLookup(a.productId);
              return (
                <li key={a.productId} className="flex items-center justify-between gap-2 px-3 py-2">
                  <ProductLabel
                    name={info.name}
                    serviceRoleCode={info.serviceRoleCode}
                    className="min-w-0 flex-1 text-sm"
                  />
                  <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => removeActivity(a.productId)}
                    disabled={disabled}
                    aria-label="Remove activity"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="space-y-3 rounded-md border p-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Invoicing
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <DateField
            label="Invoice from"
            value={value.invoiceStart}
            placeholder="When the SOW starts"
            disabled={disabled}
            onChange={(invoiceStart) => onChange({ ...value, invoiceStart })}
          />
          <DateField
            label="Invoice until"
            value={value.invoiceEnd}
            placeholder="No end"
            disabled={disabled}
            onChange={(invoiceEnd) => onChange({ ...value, invoiceEnd })}
          />
        </div>
        <div className="space-y-2">
          {/* The label used to read "Bill on its own invoice", which named
              only the THIRD option — so "Bill on its own invoice:
              Consolidate" contradicted itself. It names the question now, and
              the options answer it. */}
          <Label htmlFor="engagement-consolidate">Invoice grouping</Label>
          <Select
            value={value.consolidate === null ? INHERIT : value.consolidate ? 'yes' : 'no'}
            onValueChange={(v) =>
              onChange({ ...value, consolidate: v === INHERIT ? null : v === 'yes' })
            }
            disabled={disabled}
          >
            <SelectTrigger id="engagement-consolidate">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* Retainers are gathered per customer either way; the
                  customer's own flag decides whether their HOURS join too. */}
              <SelectItem value={INHERIT}>From customer (grouped)</SelectItem>
              <SelectItem value="yes">Group with the customer’s others</SelectItem>
              <SelectItem value="no">Always its own invoice</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox
            id="engagement-manual"
            checked={value.manual ?? false}
            onCheckedChange={(checked) => onChange({ ...value, manual: checked === true })}
            disabled={disabled}
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <Label htmlFor="engagement-manual">{INVOICE_FLAG_REASON_LABELS.MANUAL_REVIEW}</Label>
            <p className="text-xs text-muted-foreground">
              Every invoice from this engagement is flagged for review and must be approved on its
              own.
            </p>
          </div>
        </div>
      </div>

      {value.billingModel === 'retainer' ? (
        <AdvancedRetainerFields
          value={value}
          roleLookup={roleLookup}
          disabled={disabled}
          onChange={onChange}
        />
      ) : null}

      <BulkAddProductsSheet
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onSubmit={addActivities}
        alreadyAddedIds={new Set(value.activities.map((a) => a.productId))}
      />
    </div>
  );
}

/**
 * Discount and overage rates, folded away.
 *
 * Both are real — a discount becomes its own negative invoice line, and
 * overage rates are what a review flag quotes when hours run past the budget —
 * but most retainers carry neither, and sitting them between the fee and the
 * scope pushed the things people DO set below the fold.
 *
 * The count in the summary is what keeps folding them honest: a set discount
 * or a priced role is visible without opening anything, so nothing is hidden,
 * only collapsed.
 */
function AdvancedRetainerFields({
  value,
  roleLookup,
  disabled,
  onChange,
}: {
  value: Extract<EngagementInput, { billingModel: 'retainer' }>;
  roleLookup: (id: string) => string;
  disabled?: boolean;
  onChange: (next: EngagementInput) => void;
}) {
  const setCount = (value.discount ? 1 : 0) + (value.overageRates?.length ?? 0);
  // Open when something is set, so an engagement that HAS a discount does not
  // hide it behind a click the reader has no reason to make.
  const [open, setOpen] = useState(setCount > 0);

  const setDiscount = (patch: Partial<NonNullable<typeof value.discount>> | null) => {
    if (patch === null) return onChange({ ...value, discount: null });
    const base = value.discount ?? { kind: 'percentage' as const, amount: 0 };
    onChange({ ...value, discount: { ...base, ...patch } });
  };

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted/50"
      >
        <ChevronRight className={cn('size-4 transition-transform', open && 'rotate-90')} />
        Discount and overage
        {setCount > 0 ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal text-foreground">
            {setCount} set
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="space-y-4 border-t px-3 py-3">
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center justify-between">
              <Label>Discount (optional)</Label>
              {value.discount ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setDiscount(null)}
                  disabled={disabled}
                >
                  Remove
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setDiscount({ kind: 'percentage', amount: 0 })}
                  disabled={disabled}
                >
                  <Plus className="size-4" /> Add discount
                </Button>
              )}
            </div>
            {value.discount ? (
              <div className="grid grid-cols-2 items-end gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Kind</Label>
                  <Select
                    value={value.discount.kind}
                    onValueChange={(v) => setDiscount({ kind: v as 'percentage' | 'fixed_amount' })}
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed_amount">Fixed amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    Amount {value.discount.kind === 'percentage' ? '(%)' : '($)'}
                  </Label>
                  <CurrencyInput
                    value={value.discount.amount}
                    onChange={(amount) => setDiscount({ amount })}
                    disabled={disabled}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <OverageRates
            value={value}
            roleLookup={roleLookup}
            disabled={disabled}
            onChange={onChange}
          />
        </div>
      ) : null}
    </div>
  );
}

function OverageRates({
  value,
  roleLookup,
  disabled,
  onChange,
}: {
  value: Extract<EngagementInput, { billingModel: 'retainer' }>;
  /** The page's lookup, not a stub: these rows name roles exactly as the T&M
   *  table and the engagement card do, and a blank label would leave the
   *  operator unable to tell which role they are pricing or removing. */
  roleLookup: (id: string) => string;
  disabled?: boolean;
  onChange: (next: EngagementInput) => void;
}) {
  const rates = value.overageRates ?? [];
  return (
    <div className="space-y-2">
      <EngagementRateTable
        label="Overage rates"
        rates={rates}
        roleLookup={roleLookup}
        disabled={disabled}
        emptyHint="No overage rates. Hours above budget are flagged for review rather than billed automatically."
        onChange={(next) => onChange({ ...value, overageRates: next.length > 0 ? next : null })}
      />
      <p className="text-xs text-muted-foreground">
        What hours ABOVE this retainer's budget bill at. Recorded now — generation raises a review
        flag rather than adding the line itself.
      </p>
    </div>
  );
}

function MetricsFields({
  value,
  disabled,
  onChange,
}: {
  value: Extract<EngagementInput, { billingModel: 'metrics' }>;
  disabled?: boolean;
  onChange: (next: EngagementInput) => void;
}) {
  const setTiers = (tiers: EngagementTier[]) => onChange({ ...value, tiers });
  const updateTier = (i: number, patch: Partial<EngagementTier>) => {
    setTiers(value.tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  };
  const addTier = () => {
    const last = value.tiers[value.tiers.length - 1];
    // The new band opens where the previous one closed, so the ladder stays
    // contiguous by construction — the server rejects a gap outright.
    const fromQty = last?.toQty ?? (last ? last.fromQty + 1 : 0);
    setTiers([
      ...value.tiers.map((t, idx) =>
        idx === value.tiers.length - 1 && t.toQty == null ? { ...t, toQty: fromQty } : t,
      ),
      { fromQty, toQty: null, price: 0 },
    ]);
  };
  const removeTier = (i: number) => setTiers(value.tiers.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="engagement-metric">Metric</Label>
        <Input
          id="engagement-metric"
          value={value.metricName}
          onChange={(e) => onChange({ ...value, metricName: e.target.value })}
          placeholder="e.g. Transactions processed"
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Pricing tiers ({value.tiers.length})</Label>
          <Button type="button" size="sm" variant="outline" onClick={addTier} disabled={disabled}>
            <Plus className="size-4" /> Add tier
          </Button>
        </div>
        {value.tiers.length === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            No tiers yet. Add one to price a band of the metric.
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {value.tiers.map((tier, i) => (
              <li
                // Bands are ordered and their bounds are edited in place, so
                // the index IS the identity here — keying on fromQty would
                // remount the row mid-edit.
                key={`tier-${i}`}
                className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2 px-3 py-2"
              >
                <div className="space-y-1">
                  <Label className="text-xs">From</Label>
                  <QuantityInput
                    value={tier.fromQty}
                    onChange={(fromQty) => updateTier(i, { fromQty: fromQty ?? 0 })}
                    disabled={disabled}
                    ariaLabel={`Tier ${i + 1} from quantity`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To</Label>
                  <QuantityInput
                    value={tier.toQty}
                    onChange={(toQty) => updateTier(i, { toQty })}
                    placeholder="No limit"
                    disabled={disabled}
                    ariaLabel={`Tier ${i + 1} to quantity`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Price</Label>
                  <CurrencyInput
                    value={tier.price}
                    onChange={(price) => updateTier(i, { price })}
                    disabled={disabled}
                    aria-label={`Tier ${i + 1} price`}
                  />
                </div>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => removeTier(i)}
                  disabled={disabled}
                  aria-label={`Remove tier ${i + 1}`}
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">
          A flat price per band of the metric's quantity. Bands must meet exactly — the last one can
          be left open. Metrics engagements are recorded now; nothing is invoiced from them yet.
        </p>
      </div>
    </div>
  );
}

/**
 * A metric-band bound. `type="text"` with `inputMode="numeric"`, never
 * `type="number"`: a number input edits its value on scroll, so passing over
 * the form with the wheel silently repricing a tier is a real failure mode
 * (see docs — no `type="number"` inputs anywhere in this app).
 *
 * Empty means "no limit" and maps to null, which is only meaningful on the
 * top band.
 */
function QuantityInput({
  value,
  placeholder,
  disabled,
  ariaLabel,
  onChange,
}: {
  value: number | null;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (next: number | null) => void;
}) {
  return (
    <Input
      type="text"
      inputMode="numeric"
      value={value ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => {
        const raw = e.target.value.trim();
        if (raw === '') return onChange(null);
        const parsed = Number(raw);
        // `Number('')` is 0 and `Number('abc')` is NaN — both would write a
        // bound nobody typed, so a non-finite parse is simply ignored.
        if (Number.isFinite(parsed)) onChange(parsed);
      }}
    />
  );
}

function DateField({
  label,
  value,
  placeholder,
  disabled,
  onChange,
}: {
  label: string;
  value: string | null | undefined;
  placeholder: string;
  disabled?: boolean;
  onChange: (next: string | null) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <DatePicker
        value={value ?? ''}
        onChange={(v) => onChange(v || null)}
        placeholder={placeholder}
        clearable
        disabled={disabled}
      />
    </div>
  );
}
